import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Inbox, SearchX, List, CalendarDays } from "lucide-react";
import { Header } from "@/components/todo/Header";
import { StatsDashboard } from "@/components/todo/StatsDashboard";
import { AddTaskForm } from "@/components/todo/AddTaskForm";
import { Toolbar } from "@/components/todo/Toolbar";
import { TaskItem } from "@/components/todo/TaskItem";
import { CalendarView } from "@/components/todo/CalendarView";
import { Footer } from "@/components/todo/Footer";
import { InstallBanner } from "@/components/todo/InstallBanner";
import { useApp } from "@/context/AppContext";
import { useAlerts } from "@/context/AlertsContext";
import { FloatingTimer } from "@/components/todo/FloatingTimer";
import { RingingOverlay } from "@/components/todo/RingingOverlay";
import { tasksApi } from "@/lib/api";

const EmptyState = ({ noResults }) => {
  const { t } = useApp();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center text-center py-16 px-6"
      data-testid="empty-state"
    >
      <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
        {noResults ? (
          <SearchX className="w-8 h-8 text-muted-foreground" />
        ) : (
          <Inbox className="w-8 h-8 text-primary" />
        )}
      </div>
      <h3 className="font-heading font-semibold text-lg">
        {noResults ? t("noResults") : t("emptyTitle")}
      </h3>
      {!noResults && <p className="text-sm text-muted-foreground mt-1 max-w-xs">{t("emptyDesc")}</p>}
    </motion.div>
  );
};

export default function TodoApp() {
  const { t } = useApp();
  const { setTasks: setAlertTasks, startTimer, setReload } = useAlerts();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState(
    () => (new URLSearchParams(window.location.search).get("view") === "calendar" ? "calendar" : "list")
  );

  const load = async () => {
    try {
      const data = await tasksApi.list();
      setTasks(data);
    } catch (e) {
      toast.error("Fehler beim Laden / Loading error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Reload-Funktion für Snooze/Push-Updates registrieren
  useEffect(() => {
    setReload(load);
  }, [setReload]);

  // Aufgaben mit dem Wecker/Erinnerungs-System teilen
  useEffect(() => {
    setAlertTasks(tasks);
  }, [tasks, setAlertTasks]);

  const addTask = async (payload) => {
    const created = await tasksApi.create(payload);
    setTasks((prev) => [created, ...prev]);
    toast.success(t("added"));
  };

  const toggleTask = async (task) => {
    const updated = await tasksApi.update(task.id, { completed: !task.completed });
    setTasks((prev) => prev.map((x) => (x.id === task.id ? updated : x)));
  };

  const editTask = async (id, payload) => {
    const updated = await tasksApi.update(id, payload);
    setTasks((prev) => prev.map((x) => (x.id === id ? updated : x)));
    toast.success(t("updated"));
  };

  // Stilles Update (z. B. Checklisten-Punkt abhaken) ohne Toast
  const patchTask = async (id, payload) => {
    const updated = await tasksApi.update(id, payload);
    setTasks((prev) => prev.map((x) => (x.id === id ? updated : x)));
  };

  const deleteTask = async (task) => {
    await tasksApi.remove(task.id);
    setTasks((prev) => prev.filter((x) => x.id !== task.id));
    toast.success(t("deleted"));
  };

  const deleteCompleted = async () => {
    await tasksApi.removeCompleted();
    setTasks((prev) => prev.filter((x) => !x.completed));
    toast.success(t("deleted"));
  };

  const deleteAll = async () => {
    await tasksApi.removeAll();
    setTasks([]);
    toast.success(t("deleted"));
  };

  const stats = useMemo(() => {
    const completed = tasks.filter((x) => x.completed).length;
    return { total: tasks.length, completed, open: tasks.length - completed };
  }, [tasks]);

  const visible = useMemo(() => {
    let list = [...tasks];
    if (filter === "active") list = list.filter((x) => !x.completed);
    if (filter === "done") list = list.filter((x) => x.completed);

    const q = search.trim().toLowerCase();
    if (q) list = list.filter((x) => x.title.toLowerCase().includes(q));

    list.sort((a, b) => {
      switch (sort) {
        case "oldest":
          return new Date(a.created_at) - new Date(b.created_at);
        case "alpha":
          return a.title.localeCompare(b.title);
        case "priority": {
          const rank = { high: 3, medium: 2, low: 1 };
          const diff = (rank[b.priority] || 2) - (rank[a.priority] || 2);
          return diff !== 0 ? diff : new Date(b.created_at) - new Date(a.created_at);
        }
        case "doneLast":
          return (a.completed === b.completed)
            ? new Date(b.created_at) - new Date(a.created_at)
            : a.completed - b.completed;
        case "newest":
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    return list;
  }, [tasks, filter, search, sort]);

  return (
    <div className="min-h-screen bg-background">
      <Header tasks={tasks} />
      <FloatingTimer />
      <RingingOverlay />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <InstallBanner />
        <StatsDashboard total={stats.total} open={stats.open} completed={stats.completed} />

        <div className="mb-6">
          <AddTaskForm onAdd={addTask} />
        </div>

        {/* Ansicht umschalten: Liste / Kalender */}
        <div className="flex justify-center mb-5">
          <div className="inline-flex p-1 rounded-xl bg-secondary" data-testid="view-toggle">
            <button
              data-testid="view-list-btn"
              onClick={() => setView("list")}
              className={`flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition-all ${
                view === "list" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <List className="w-4 h-4" /> {t("viewList")}
            </button>
            <button
              data-testid="view-calendar-btn"
              onClick={() => setView("calendar")}
              className={`flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition-all ${
                view === "calendar" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <CalendarDays className="w-4 h-4" /> {t("viewCalendar")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16" data-testid="loading">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : view === "calendar" ? (
          <CalendarView
            tasks={tasks}
            onToggle={toggleTask}
            onDelete={deleteTask}
            onEdit={editTask}
            onUpdate={patchTask}
            onStartTimer={(tk) => startTimer(25 * 60, tk.title)}
          />
        ) : (
          <>
            <div className="mb-5">
              <Toolbar
                filter={filter}
                setFilter={setFilter}
                search={search}
                setSearch={setSearch}
                sort={sort}
                setSort={setSort}
                onDeleteCompleted={deleteCompleted}
                onDeleteAll={deleteAll}
                hasCompleted={stats.completed > 0}
                hasTasks={stats.total > 0}
              />
            </div>

            {visible.length === 0 ? (
              <EmptyState noResults={search.trim().length > 0 || (filter !== "all" && tasks.length > 0)} />
            ) : (
              <ul className="flex flex-col gap-3" data-testid="task-list">
                <AnimatePresence mode="popLayout">
                  {visible.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onToggle={toggleTask}
                      onDelete={deleteTask}
                      onEdit={editTask}
                      onUpdate={patchTask}
                      onStartTimer={(tk) => startTimer(25 * 60, tk.title)}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
